/**
 * Routes for the operations console at /lp-console (built under /lp-console/staging from 21 Sep 2026; swapped in as
 * the console on 26 Sep 2026). View As simulates the other roles for Admin. Pages that already exist are mounted here unchanged, per the handoff's "preserve accepted screens".
 * A role that the §4 table does not give an entry cannot open it by URL either (§2, §19). Open to every staff role
 * (25 Sep 2026, ahead of the cutover): each page is still guarded by the role's own entries.
 */
import { Navigate, Route, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { RoleProtectedRoute } from '@/components/admin/RoleProtectedRoute';
import { AdminProtectedRoute } from '@/components/admin/AdminProtectedRoute';
import { Toaster as SonnerToaster } from 'sonner';
import StagingLayout, { useStaging } from './StagingLayout';
import StagingDashboard from './pages/StagingDashboard';
import NotificationCenter from './pages/NotificationCenter';
import ResourcesPage from './pages/ResourcesPage';
import PendingPage from './pages/PendingPage';
import ReportsPage from './pages/ReportsPage';
import BusinessControls from './pages/BusinessControls';
import EditingPipeline from './pages/EditingPipeline';
import BookingsPage from './pages/BookingsPage';
import FullBooking from './pages/FullBooking';
import { ClientsIndex, ClientRecord } from './pages/ClientsPage';
import TalentPage from './pages/TalentPage';
import { UsersRolesPage, MyProfilePage } from './pages/TeamPages';
import MessagesPage from './pages/MessagesPage';
import { canOpenPath, forwardPath, FORWARDS, ROLES, STAGING_ROOT } from '@/lib/staging/roleNav';
import TalentDetails from '@/pages/admin/TalentDetails';
import BillingWorkbench from '@/pages/admin/BillingWorkbench';
import BillingClientView from '@/pages/admin/BillingClientView';
import CoordinationWorkbench from '@/pages/admin/CoordinationWorkbench';
import CoordinationBooking from '@/pages/admin/CoordinationBooking';
import InsuranceRequests from './pages/InsuranceRequests';
import ReferralAnalytics from '@/pages/admin/ReferralAnalytics';
import Reviews from '@/pages/admin/Reviews';
import AuditPage from './pages/AuditPage';
import SweepstakesEntries from '@/pages/admin/SweepstakesEntries';
import Testing from '@/pages/admin/Testing';

/** The simulated role must be allowed the entry too, so View As shows exactly what that role could reach. */
function Allowed({ children }: { children: ReactNode }) {
  const { role } = useStaging();
  if (!canOpenPath(role, useLocation().pathname)) return <Navigate to={`${STAGING_ROOT}/dashboard`} replace />;
  return <>{children}</>;
}

const wrap = (el: ReactNode) => <Allowed>{el}</Allowed>;

function Forward() {
  const { pathname, search } = useLocation();
  return <Navigate to={forwardPath(pathname, search)} replace />;
}

/** Rendered under /lp-console/* beside the login and invite pages. */
export function stagingRoutes() {
  return (
    <Route element={<AdminProtectedRoute><RoleProtectedRoute allowedRoles={ROLES}><StagingLayout /><SonnerToaster position="top-right" richColors closeButton /></RoleProtectedRoute></AdminProtectedRoute>}>
      <Route path="dashboard" element={<StagingDashboard />} />
      <Route path="bookings" element={wrap(<BookingsPage />)} />
      <Route path="bookings/:bookingId" element={wrap(<FullBooking />)} />
      <Route path="clients" element={wrap(<ClientsIndex />)} />
      <Route path="clients/:clientId" element={wrap(<ClientRecord />)} />
      <Route path="talent" element={wrap(<TalentPage />)} />
      <Route path="talent/:talentId" element={wrap(<TalentDetails />)} />
      <Route path="messages" element={wrap(<MessagesPage />)} />
      <Route path="billing" element={wrap(<BillingWorkbench />)} />
      <Route path="billing/:bookingId" element={wrap(<BillingClientView />)} />
      <Route path="coordination" element={wrap(<CoordinationWorkbench />)} />
      <Route path="coordination/:bookingId" element={wrap(<CoordinationBooking />)} />
      {/* The Editing pipeline (Traci's reference, 21 Oct 2026; swapped in for the Editing board 27 Oct 2026). Reached from
          the dashboard: Editing's own dashboard shows it; Admin opens it from Department Actions. */}
      <Route path="editing" element={wrap(<EditingPipeline />)} />
      {/* The preview's old address, so saved links still land on it */}
      <Route path="editing-preview" element={<Navigate to={`${STAGING_ROOT}/editing`} replace />} />
      <Route path="resources" element={wrap(<ResourcesPage />)} />
      <Route path="notifications" element={wrap(<NotificationCenter />)} />
      <Route path="insurance" element={wrap(<InsuranceRequests />)} />
      <Route path="referrals" element={wrap(<ReferralAnalytics />)} />
      <Route path="reviews" element={wrap(<Reviews />)} />
      <Route path="controls" element={wrap(<BusinessControls />)} />
      <Route path="reports" element={wrap(<ReportsPage />)} />
      <Route path="users" element={wrap(<UsersRolesPage />)} />
      {/* My Profile: every role, from the name in the top bar (Traci: change name, email and password yourself). */}
      <Route path="profile" element={<MyProfilePage />} />
      <Route path="audit" element={wrap(<AuditPage />)} />
      {/* Old-console pages with no new home yet, Admin only (26 Sep 2026): Leads and the Testing tools. */}
      <Route path="sweepstakes" element={wrap(<SweepstakesEntries />)} />
      <Route path="testing" element={wrap(<Testing />)} />
      {Object.keys(FORWARDS).map((k) => <Route key={k} path={`${k}/*`} element={<Forward />} />)}
      <Route path="*" element={<Navigate to={`${STAGING_ROOT}/dashboard`} replace />} />
    </Route>
  );
}
